import { DragDropEvent } from "services/dragdrop/DragDropContext";
import { EmployeeAvailabilityType, EventsByResourceType, EventsListType, EventType, ResourceGroupType, SchedulerDraggableId, SchedulerDroppableId, SchedulingJobType } from "../SchedulingTypes";
import { EnvironmentSettingsType } from "services/settings/SettingsTypes";
import { t } from "i18next";
import { v4 as uuidv4 } from 'uuid';
import { calculateDurationMinutesFromDecimal, convertDayKeyAndTimeSlotToEndDateTimeString, convertDayKeyAndTimeSlotToStartDateTimeString, generateTimeKeyFromDateTimeString } from "./schedulingDateUtils";
import { convertLocalDateTimeToUTC } from "internationalization/timezoneConversions";
import { saveData } from "services/api/saveData";
import { formatDate } from "services/utils/dateTimeUtils";
import { DayPassCheckItem } from "services/subcontractors/useSubcontractorDayPassCheck";

// Finds the resource of a resource id in the resource groups
const findResource = (resourceGroups: ResourceGroupType[], resourceId: string | number) => {
    for (const group of resourceGroups) {
        const resource = group.resources.find(res => String(res.id) === String(resourceId));
        if (resource) return resource;
    }
    return null;
};

// Checks the day passes when the destination resource is a subcontractor
const checkSubcontractorDay = async (
    resourceGroups: ResourceGroupType[],
    destinationResourceId: string | number,
    destinationDay: string,
    checkDayPasses?: (items: DayPassCheckItem[]) => Promise<boolean>
): Promise<boolean> => {
    const resource = findResource(resourceGroups, destinationResourceId);
    if (!checkDayPasses || !resource?.is_subcontractor_role) return true;
    return checkDayPasses([{ resource_id: destinationResourceId, dates: [destinationDay] }]);
};

export const handleDragJobInboxJobToResource = async (
    dragResult: DragDropEvent<SchedulerDraggableId, SchedulerDroppableId>,
    events: EventsListType,
    eventsByResource: EventsByResourceType,
    resourceGroups: ResourceGroupType[],
    userLocale: string,
    userTimezone: string,
    environmentSettings: EnvironmentSettingsType,
    setFloatingAlert: Function,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    employeeAvailability: EmployeeAvailabilityType,
    onRefresh: () => void,
    checkDayPasses?: (items: DayPassCheckItem[]) => Promise<boolean>,
    handleDayPassError?: (error: any) => boolean
) => {

    // Get the values from the drag result
    const { source, destination, draggableId } = dragResult;

    if (!destination) return;

    // Get the source type, destination type and draggable type
    const sourceType = source?.droppableId?.type
    const destinationType = destination.droppableId.type;
    const draggableType = draggableId.type;

    // Handle drag from the job inbox
    if (sourceType === 'jobInbox' && draggableType === 'jobInbox') {

        // Handle drag to a timeslot
        if (destinationType === 'timeSlot') {

            // Get the destination values
            const { resourceId: destinationResourceId, day: destinationDay, timeSlot: destinationTimeSlot } = destination.droppableId;

            // Warn and stop when the subcontractor of the row has no day pass for this day
            if (!(await checkSubcontractorDay(resourceGroups, destinationResourceId, destinationDay, checkDayPasses))) return;

            // Try splitting the job across the employee's timetable first. Only picks up when the
            // employee_timetable feature + scheduling_show_employee_timetable setting are both
            // active and the target resource has an applicable, working day - falls through to
            // the regular single-appointment flow below for every other case
            const splitHandled = await tryCreateAppointmentsFromEmployeeTimeTable(
                draggableId.job, destinationResourceId, destinationDay, userTimezone, employeeAvailability, onRefresh, setFloatingAlert, handleDayPassError
            );
            if (splitHandled) return;

            // When "schedule appointments for the whole day by default" is on, ignore wherever was
            // dropped and use the employee's roster (or the displayed scheduling times as a
            // fallback) for this day instead - splitting (above) still takes priority over this
            const wholeDayTimes = environmentSettings.scheduling_default_appointment_whole_day
                ? getWholeDayTimes(employeeAvailability, destinationResourceId, destinationDay, environmentSettings)
                : null;

            let startTime = destinationTimeSlot;
            let durationMinutes;

            if (wholeDayTimes) {
                startTime = wholeDayTimes.startTime;
                durationMinutes = calculateMinutesBetween(wholeDayTimes.startTime, wholeDayTimes.endTime);

            // Convert the estimated duration of the job into duration minutes
            } else if (draggableId.job.estimated_duration != null) {
                durationMinutes = calculateDurationMinutesFromDecimal(draggableId.job.estimated_duration);
            } else {
                // Fallback to 1 hour if no estimated duration is set
                durationMinutes = 60;
            }

            // Create the event and save the appointment
            await createEventAndSaveAppointmentAfterDrag(
                draggableId.job,
                resourceGroups,
                destinationResourceId,
                destinationDay,
                startTime,
                durationMinutes,
                userLocale,
                userTimezone,
                setEvents,
                setEventsByResource,
                setFloatingAlert,
                handleDayPassError,
            );
        }

        // Handle drag to a day list
        if (destinationType === 'dayList') {

            // Get the destination values
            const { resourceId: destinationResourceId, day: destinationDay } = destination.droppableId;

            // Warn and stop when the subcontractor of the row has no day pass for this day
            if (!(await checkSubcontractorDay(resourceGroups, destinationResourceId, destinationDay, checkDayPasses))) return;

            // Try splitting the job across the employee's timetable first, same as the timeslot branch above
            const splitHandledDayList = await tryCreateAppointmentsFromEmployeeTimeTable(
                draggableId.job, destinationResourceId, destinationDay, userTimezone, employeeAvailability, onRefresh, setFloatingAlert, handleDayPassError
            );
            if (splitHandledDayList) return;

            // When "schedule appointments for the whole day by default" is on, ignore the drop
            // entirely and use the employee's roster (or the displayed scheduling times as a
            // fallback) for this day - same override as the timeslot branch above, splitting
            // still takes priority (handled above)
            const wholeDayTimesForDayList = environmentSettings.scheduling_default_appointment_whole_day
                ? getWholeDayTimes(employeeAvailability, destinationResourceId, destinationDay, environmentSettings)
                : null;

            let startTime;
            let durationMinutes;

            if (wholeDayTimesForDayList) {
                startTime = wholeDayTimesForDayList.startTime;
                durationMinutes = calculateMinutesBetween(wholeDayTimesForDayList.startTime, wholeDayTimesForDayList.endTime);

            } else {

                // Use the from time of the displayed scheduling times as the default start time
                const defaultStartTime = environmentSettings.scheduling_display_start_time?.slice(0, 5) || "08:00";

                // Get the existing events for this day and resource
                const existingEventKeys = eventsByResource[destinationResourceId] || [];
                const existingEvents = existingEventKeys
                    .map(eventKey => events[eventKey])
                    .filter(event => event && event.dayKey === destinationDay);

                // If there is already an event for this day, set the end time of the last event as the start time
                if (existingEvents.length > 0) {

                    // Sort the events on start time, fallback to the default start time
                    existingEvents.sort((a, b) =>
                        new Date(a.startTime ?? defaultStartTime).getTime() -
                        new Date(b.startTime ?? defaultStartTime).getTime()
                    );

                    // Get the end time of the last event
                    startTime = existingEvents[existingEvents.length - 1]?.endTime ?? defaultStartTime;

                // When no events exist, use the default start time
                } else {
                    startTime = defaultStartTime;
                }

                // If an estimated duration for the job is set, use it as duration
                if (draggableId.job.estimated_duration != null) {
                    durationMinutes = calculateDurationMinutesFromDecimal(draggableId.job.estimated_duration);

                // Otherwise, use the default duration of 1 hour
                } else {
                    durationMinutes = 60;
                }
            }

            // Create the event and save the appointment
            await createEventAndSaveAppointmentAfterDrag(
                draggableId.job,
                resourceGroups,
                destinationResourceId,
                destinationDay,
                startTime,
                durationMinutes,
                userLocale,
                userTimezone,
                setEvents,
                setEventsByResource,
                setFloatingAlert,
                handleDayPassError,
            );
        }
    }
};

// Determines the employee's roster start/end time for this resource and day, falling back to the
// scheduling display times when there's no (working) roster day - used for "schedule appointments
// for the whole day by default", which ignores wherever a job was dropped
const getWholeDayTimes = (
    employeeAvailability: EmployeeAvailabilityType,
    destinationResourceId: string | number,
    destinationDay: string,
    environmentSettings: EnvironmentSettingsType
): { startTime: string; endTime: string } | null => {
    const dayAvailability = employeeAvailability[destinationResourceId]?.[destinationDay];

    if (dayAvailability?.is_working && dayAvailability.start_time && dayAvailability.end_time) {
        return { startTime: dayAvailability.start_time.slice(0, 5), endTime: dayAvailability.end_time.slice(0, 5) };
    }

    const displayStartTime = environmentSettings.scheduling_display_start_time;
    const displayEndTime = environmentSettings.scheduling_display_end_time;
    if (displayStartTime && displayEndTime) {
        return { startTime: displayStartTime.slice(0, 5), endTime: displayEndTime.slice(0, 5) };
    }

    return null;
};

// Calculates the whole number of minutes between two "HH:MM" times
const calculateMinutesBetween = (startTime: string, endTime: string): number => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    return (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
};

// Attempts to create appointments split across an employee's timetable instead of a single
// appointment. Returns true if it successfully handled the drag (caller should stop there),
// false if the caller should fall back to the regular single-appointment flow - which covers
// every case where the feature/setting are inactive, the resource has no applicable/working
// timetable day, the job has no estimated_duration, or the request itself failed
const tryCreateAppointmentsFromEmployeeTimeTable = async (
    draggableJob: SchedulingJobType,
    destinationResourceId: string | number,
    destinationDay: string,
    userTimezone: string,
    employeeAvailability: EmployeeAvailabilityType,
    onRefresh: () => void,
    setFloatingAlert: Function,
    handleDayPassError?: (error: any) => boolean,
): Promise<boolean> => {
    // Don't re-check environmentSettings.scheduling_show_employee_timetable here: employeeAvailability
    // is already only ever populated (per resource/day) when the employee_timetable feature and this
    // setting are both active - that's enforced backend-side in Get_Events. Re-checking the cached
    // environmentSettings value here as well is redundant and can go stale independently (e.g. right
    // after toggling the setting, before the session's settings context refreshes), silently disabling
    // the split even though the availability data backing it is already correct.
    const dayAvailability = employeeAvailability[destinationResourceId]?.[destinationDay];
    const eligibleForSplit = !!dayAvailability?.is_working && draggableJob.estimated_duration != null;

    if (!eligibleForSplit) return false;

    try {
        const response = await saveData({
            apiUrl: 'post_appointments_from_employee_timetable',
            data: {
                job: draggableJob.id,
                resource_id: destinationResourceId,
                drop_date: destinationDay,
                estimated_duration: draggableJob.estimated_duration,
                timezone: userTimezone,
            }
        });

        if (response?.status === 201) {
            // A full delta refetch is simplest and safest here - rebuilding the optimistic
            // single-event update for an unknown number of created appointments isn't worth it
            onRefresh();
            setFloatingAlert({ type: 'success', message: t('scheduling.scheduling_board.inbox_job_split_across_timetable_alert') });
            return true;
        }
    } catch (error) {
        // A refusal for missing subcontractor day passes shows the modal and stops the drag
        if (handleDayPassError && handleDayPassError(error)) return true;

        // Any other failure (e.g. not enough working days within the lookahead window) falls through
        // to the regular single-appointment flow, so the drag never silently does nothing
    }

    return false;
};

// Creates an event and saves the appointment after the drag
const createEventAndSaveAppointmentAfterDrag = async (
    draggableJob: SchedulingJobType,
    resourceGroups: ResourceGroupType[],
    destinationResourceId: string | number,
    destinationDay: string,
    destinationStartTime: string,
    durationMinutes: number,
    userLocale: string,
    userTimezone: string,
    setEvents: React.Dispatch<React.SetStateAction<EventsListType>>,
    setEventsByResource: React.Dispatch<React.SetStateAction<EventsByResourceType>>,
    setFloatingAlert: Function,
    handleDayPassError?: (error: any) => boolean
) => {
    // Create a temporarily appointment uuid and temporarily event key
    const appointmentUuid = uuidv4();
    const temporarilyEventKey = `appointment-${appointmentUuid}-${destinationResourceId}`;

    // Convert the day keys and timeslots to local datetime objects
    const convertedStartDateTime = convertDayKeyAndTimeSlotToStartDateTimeString(destinationDay, destinationStartTime, userLocale, userTimezone);
    const convertedEndDateTime = convertDayKeyAndTimeSlotToEndDateTimeString(destinationDay, destinationStartTime, durationMinutes, userLocale, userTimezone);

    // Create a new event
    const temporarilyEvent: EventType = {
        key: temporarilyEventKey,
        type: 'appointment',
        appointmentUuid: appointmentUuid,
        appointmentId: null,
        locked: false,
        job: {
            id: draggableJob.id!,
            client_name: draggableJob.client_name ?? undefined,
            type_name: draggableJob.type_name ? draggableJob.type_name : undefined,
            work_location_street: draggableJob.work_location_street ?? undefined,
            work_location_house_number: draggableJob.work_location_house_number ?? undefined,
            work_location_postal_code: draggableJob.work_location_postal_code ?? undefined,
            work_location_city: draggableJob.work_location_city ?? undefined,
            internal_reference: draggableJob.internal_reference ?? null,
            external_reference: draggableJob.external_reference ?? null,
            number: draggableJob.number ?? null,
            status_name: draggableJob.status_name ?? null,
            status_condition: draggableJob.status_condition ?? null,
            resolution: draggableJob.resolution ?? null,
            main_description: draggableJob.main_description ? draggableJob.main_description : null,
            target_date: draggableJob.target_date ?? null,
            estimated_duration: draggableJob.estimated_duration ?? null,
            labels: draggableJob.labels ? draggableJob.labels : null,
        },
        resourceId: destinationResourceId,
        dayKey: destinationDay,
        timeSlot: destinationStartTime,
        startDateTime: convertedStartDateTime,
        endDateTime: convertedEndDateTime,
        durationMinutes: durationMinutes,
        status: 'scheduled',
        custom_color: null,
        deleted: false,
    };

    // Add the temporarily event to the events state
    setEvents(prev => ({
        ...prev,
        [temporarilyEventKey]: temporarilyEvent,
    }));

    // Add the event to the destination resource state
    setEventsByResource(prev => {
        const newEventsByResource = { ...prev };

        // Create a new array for the destination resource if it doesn't exists
        if (!newEventsByResource[destinationResourceId]) {
            newEventsByResource[destinationResourceId] = [];
        }

        // Add the temporarily event key to the resource
        newEventsByResource[destinationResourceId].push(temporarilyEventKey);

        return newEventsByResource;
    });

    // Convert the local datetimes to utc datetimes
    const utcStartDateTime = convertLocalDateTimeToUTC(convertedStartDateTime, userLocale, userTimezone);
    const utcEndDateTime = convertLocalDateTimeToUTC(convertedEndDateTime, userLocale, userTimezone);

    // Configure the submit data for the appointment
    const newAppointment = {
        id: null,
        uuid: appointmentUuid,
        job: draggableJob.id,
        resource_ids: [destinationResourceId],
        timezone: userTimezone,
        start_datetime: utcStartDateTime,
        end_datetime: utcEndDateTime,
    };

    // Removes the temporarily event again when the save was refused
    const removeTemporarilyEvent = () => {
        setEvents(prev => {
            const currentEvents = { ...prev };
            delete currentEvents[temporarilyEventKey];
            return currentEvents;
        });
        setEventsByResource(prev => {
            const currentEventsByResource = { ...prev };
            currentEventsByResource[destinationResourceId] = (currentEventsByResource[destinationResourceId] || []).filter(key => key !== temporarilyEventKey);
            return currentEventsByResource;
        });
    };

    // Save the new appointment as a constant so its narrowing also holds inside the
    // state callbacks below, a refusal for missing day passes shows the modal
    const response = await saveData({ apiUrl: 'post_appointment', data: newAppointment }).catch((error) => {
        removeTemporarilyEvent();
        if (!(handleDayPassError && handleDayPassError(error))) {
            setFloatingAlert({ type: 'danger', message: t('scheduling.scheduling_board.appointment_save_failed_alert') });
        }
        return undefined;
    });
    if (!response) return;

    // Handle successful response, update temporarily event key with new appointment id to prevent duplicates
    if (response?.status === 201) {
        
        // Set the new appointment id and updated event key
        const newAppointmentId = response.data.id;
        const updatedEventKey = `appointment-${newAppointmentId}-${destinationResourceId}`;

        // Update the event key in de events state
        setEvents(prev => {
            const currentEvents = { ...prev };

            // Find the temporarily event with the temporarily key
            const temporarilyEvent = currentEvents[temporarilyEventKey];

            // Only update if the temporarily event is an appointment
            if (temporarilyEvent.type === 'appointment') {
                // Create the new updated event with the updated key
                const updatedEvent: EventType = {
                    ...temporarilyEvent,
                    key: updatedEventKey,
                    appointmentId: newAppointmentId,
                    startTime: generateTimeKeyFromDateTimeString(convertedStartDateTime),
                    endTime: generateTimeKeyFromDateTimeString(convertedEndDateTime),
                    status: response.data.status,
                };

                // Remove the old temporarily event key from the state
                delete currentEvents[temporarilyEventKey];

                // Add the updated event with the right backend key
                currentEvents[updatedEventKey] = updatedEvent;

                return currentEvents;

            }

            return currentEvents;
        });

        // Update the events by resource state, remove the temporarily event and add the updated event met the final key
        setEventsByResource(prev => {
            const currentEventsByResource = { ...prev };

            // Get the list of the appropriate resource
            const resourceEvents = currentEventsByResource[destinationResourceId];

            // Get the event index of the temporarily event
            const eventIndex = resourceEvents.indexOf(temporarilyEventKey);

            // Replace the temporarily event key with the new key
            if (eventIndex > -1) {
                resourceEvents[eventIndex] = updatedEventKey;
            }

            return currentEventsByResource;
        });

        // Find the name of the resource based on the destination resource id
        const findResourceName = (resourceId: number | string): string | null => {
            for (const group of resourceGroups) {
                const resource = group.resources.find(res => res.id === resourceId);
                if (resource) {
                    return resource.name
                }
            }

            return '';
        };

        // Show a floating alert on success
        setFloatingAlert({
            type: 'success',
            message: t('scheduling.scheduling_board.inbox_job_dragged_to_resource_alert', { resource_name: findResourceName(destinationResourceId), date: formatDate(destinationDay, userLocale, 'writtenMonth'), time: destinationStartTime })
        });
    }
};